import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import type { GinTableMasterResponse } from '@wedding-tech/api-client'
import {
  getApiV1WeddingsByWeddingIdGuestsQueryKey,
  getApiV1WeddingsByWeddingIdTablesMasterAllOptions,
  getApiV1WeddingsByWeddingIdTablesMasterAllQueryKey,
  postApiV1WeddingsByWeddingIdAdminTablesAssignMutation,
  postApiV1WeddingsByWeddingIdAdminTablesUnassignMutation,
} from '@wedding-tech/api-client/tanstack'
import { Maximize2, X, ZoomIn, ZoomOut } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'

export interface KioskTablePickerProps {
  isOpen: boolean
  onClose: () => void
  guest: {
    id: string
    fullName: string
    currentTableId?: string // master table ID of current assignment (for highlight + capacity check)
    tableNumber?: string // shown in header so staff sees current seat
    followers?: number // used for capacity check; defaults to 1
  }
  onSuccess: (
    assignment: { tableId: string; tableNumber: string } | null,
  ) => void
}

type Mode = 'map' | 'list'

interface CanvasProps {
  tables: GinTableMasterResponse[]
  selectedTableId: string | null
  currentTableId?: string
  isTableFull: (t: GinTableMasterResponse) => boolean
  onSelect: (id: string) => void
}

const SCALE_MIN = 0.15
const SCALE_MAX = 5
const MOVE_THRESHOLD = 5 // px — below this, treat as tap not drag

const MapCanvas = ({
  tables,
  selectedTableId,
  currentTableId,
  isTableFull,
  onSelect,
}: CanvasProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)

  // Transform stored in refs to avoid React re-renders during pan/zoom
  const transform = useRef({ scale: 1, x: 0, y: 0 })
  const isPanning = useRef(false)
  const hasMoved = useRef(false)
  const panStart = useRef({ clientX: 0, clientY: 0, originX: 0, originY: 0 })
  const pinchDist = useRef<number | null>(null)
  const pinchMid = useRef({ x: 0, y: 0 })

  const positioned = tables.filter(
    (t) => t.x !== undefined && t.y !== undefined,
  )

  const canvasWidth = positioned.length
    ? Math.max(...positioned.map((t) => (t.x ?? 0) + (t.width ?? 80))) + 60
    : 800
  const canvasHeight = positioned.length
    ? Math.max(...positioned.map((t) => (t.y ?? 0) + (t.height ?? 80))) + 60
    : 600

  // Apply transform directly to DOM — no React re-render on every frame
  const applyTransform = useCallback((s: number, x: number, y: number) => {
    transform.current = { scale: s, x, y }
    if (innerRef.current) {
      innerRef.current.style.transform = `translate(${x}px, ${y}px) scale(${s})`
    }
  }, [])

  const resetView = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    const { width, height } = container.getBoundingClientRect()
    if (width === 0 || height === 0 || positioned.length === 0) return
    const fitScale = Math.min(width / canvasWidth, height / canvasHeight) * 0.92
    applyTransform(
      fitScale,
      (width - canvasWidth * fitScale) / 2,
      (height - canvasHeight * fitScale) / 2,
    )
  }, [applyTransform, canvasWidth, canvasHeight, positioned.length])

  const zoomIn = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    const { width, height } = container.getBoundingClientRect()
    const cx = width / 2
    const cy = height / 2
    const { scale, x, y } = transform.current
    const ns = Math.min(scale * 1.3, SCALE_MAX)
    const ratio = ns / scale
    applyTransform(ns, cx - (cx - x) * ratio, cy - (cy - y) * ratio)
  }, [applyTransform])

  const zoomOut = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    const { width, height } = container.getBoundingClientRect()
    const cx = width / 2
    const cy = height / 2
    const { scale, x, y } = transform.current
    const ns = Math.max(scale / 1.3, SCALE_MIN)
    const ratio = ns / scale
    applyTransform(ns, cx - (cx - x) * ratio, cy - (cy - y) * ratio)
  }, [applyTransform])

  // Fit all tables into the visible container on open
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    requestAnimationFrame(() => resetView())
  }, [resetView])

  // Non-passive event listeners: wheel zoom + touchmove pan/pinch
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      const cx = e.clientX - rect.left
      const cy = e.clientY - rect.top
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1
      const { scale, x, y } = transform.current
      const ns = Math.min(Math.max(scale * factor, SCALE_MIN), SCALE_MAX)
      const ratio = ns / scale
      applyTransform(ns, cx - (cx - x) * ratio, cy - (cy - y) * ratio)
    }

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        const [t1, t2] = Array.from(e.touches)
        const dist = Math.hypot(
          t2.clientX - t1.clientX,
          t2.clientY - t1.clientY,
        )
        const mx = (t1.clientX + t2.clientX) / 2
        const my = (t1.clientY + t2.clientY) / 2
        const rect = el.getBoundingClientRect()
        const cx = mx - rect.left
        const cy = my - rect.top
        if (pinchDist.current !== null) {
          const factor = dist / pinchDist.current
          const dmx = mx - pinchMid.current.x
          const dmy = my - pinchMid.current.y
          const { scale, x, y } = transform.current
          const ns = Math.min(Math.max(scale * factor, SCALE_MIN), SCALE_MAX)
          const ratio = ns / scale
          applyTransform(
            ns,
            cx - (cx - x) * ratio + dmx,
            cy - (cy - y) * ratio + dmy,
          )
        }
        pinchDist.current = dist
        pinchMid.current = { x: mx, y: my }
      } else if (e.touches.length === 1 && isPanning.current) {
        e.preventDefault()
        const dx = e.touches[0].clientX - panStart.current.clientX
        const dy = e.touches[0].clientY - panStart.current.clientY
        if (Math.abs(dx) > MOVE_THRESHOLD || Math.abs(dy) > MOVE_THRESHOLD) {
          hasMoved.current = true
        }
        applyTransform(
          transform.current.scale,
          panStart.current.originX + dx,
          panStart.current.originY + dy,
        )
      }
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    return () => {
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('touchmove', onTouchMove)
    }
  }, [applyTransform])

  // Mouse pan
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-table]')) return
    isPanning.current = true
    hasMoved.current = false
    panStart.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      originX: transform.current.x,
      originY: transform.current.y,
    }
  }, [])

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isPanning.current) return
      const dx = e.clientX - panStart.current.clientX
      const dy = e.clientY - panStart.current.clientY
      if (Math.abs(dx) > MOVE_THRESHOLD || Math.abs(dy) > MOVE_THRESHOLD) {
        hasMoved.current = true
      }
      applyTransform(
        transform.current.scale,
        panStart.current.originX + dx,
        panStart.current.originY + dy,
      )
    },
    [applyTransform],
  )

  const onMouseUp = useCallback(() => {
    isPanning.current = false
  }, [])

  // Touch start + end
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      isPanning.current = false
      const [t1, t2] = Array.from(e.touches)
      pinchDist.current = Math.hypot(
        t2.clientX - t1.clientX,
        t2.clientY - t1.clientY,
      )
      pinchMid.current = {
        x: (t1.clientX + t2.clientX) / 2,
        y: (t1.clientY + t2.clientY) / 2,
      }
    } else if (
      e.touches.length === 1 &&
      !(e.target as HTMLElement).closest('[data-table]')
    ) {
      isPanning.current = true
      hasMoved.current = false
      panStart.current = {
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY,
        originX: transform.current.x,
        originY: transform.current.y,
      }
    }
  }, [])

  const onTouchEnd = useCallback(() => {
    isPanning.current = false
    pinchDist.current = null
  }, [])

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full cursor-grab overflow-hidden select-none active:cursor-grabbing"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div
        ref={innerRef}
        className="relative"
        style={{
          width: canvasWidth,
          height: canvasHeight,
          transformOrigin: '0 0',
        }}
      >
        {/* Dot grid */}
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'radial-gradient(#344C3D33 1px, transparent 0)',
            backgroundSize: '20px 20px',
          }}
        />

        {positioned.map((table) => {
          const left = table.x ?? 0
          const top = table.y ?? 0
          const width = table.width ?? 80
          const height = table.height ?? 80
          const radius = table.shape === 'circle' ? '50%' : 8

          if (table.type && table.type !== 'table') {
            return (
              <div
                key={table.id}
                className="border-forest-light/20 bg-forest-light/10 text-forest-light/40 pointer-events-none absolute flex items-center justify-center border text-[10px] font-bold select-none"
                style={{ left, top, width, height, borderRadius: radius }}
              >
                {table.tableNumber}
              </div>
            )
          }

          const isCurrent = table.id === currentTableId
          const isSelected = table.id === selectedTableId
          const full = isTableFull(table)

          return (
            <button
              key={table.id}
              data-table={table.id}
              type="button"
              onClick={() => {
                if (hasMoved.current) return
                onSelect(isSelected ? '' : table.id || '')
              }}
              disabled={full}
              className={[
                'absolute flex flex-col items-center justify-center border-2 transition-colors',
                full
                  ? 'border-forest-light/20 bg-forest-light/10 text-forest-light/30 cursor-not-allowed opacity-40'
                  : isSelected
                    ? 'border-rose-dark/80 bg-rose-dark shadow-rose-dark/40 text-white shadow-lg'
                    : isCurrent
                      ? 'border-rose-dark bg-rose-dark/25 text-forest-light'
                      : 'border-forest-light/20 text-forest-light hover:border-rose-dark bg-white',
              ].join(' ')}
              style={{ left, top, width, height, borderRadius: radius }}
            >
              <span className="text-sm leading-none font-bold">
                {table.tableNumber}
              </span>
              <span className="mt-0.5 text-[10px] font-semibold opacity-70">
                {table.assignedCount ?? 0}/{table.capacity ?? '?'}
              </span>
            </button>
          )
        })}
      </div>

      {/* ── Zoom controls ───────────────────────────────── */}
      <div className="absolute right-4 bottom-4 z-10 flex flex-col gap-1.5">
        <button
          type="button"
          onClick={zoomIn}
          aria-label="ซูมเข้า"
          className="border-forest-light/10 text-forest-light flex size-9 items-center justify-center rounded-xl border bg-white shadow-sm transition-all hover:bg-[#fdf4f5] active:scale-95"
        >
          <ZoomIn className="size-4" />
        </button>
        <button
          type="button"
          onClick={zoomOut}
          aria-label="ซูมออก"
          className="border-forest-light/10 text-forest-light flex size-9 items-center justify-center rounded-xl border bg-white shadow-sm transition-all hover:bg-[#fdf4f5] active:scale-95"
        >
          <ZoomOut className="size-4" />
        </button>
        <div className="bg-forest-light/10 mx-1 h-px" />
        <button
          type="button"
          onClick={resetView}
          aria-label="รีเซ็ตมุมมอง"
          className="border-forest-light/10 text-forest-light flex size-9 items-center justify-center rounded-xl border bg-white shadow-sm transition-all hover:bg-[#fdf4f5] active:scale-95"
        >
          <Maximize2 className="size-4" />
        </button>
      </div>
    </div>
  )
}

const occupancyColor = (assigned: number, capacity: number): string => {
  const pct = capacity > 0 ? assigned / capacity : 0
  if (pct >= 0.9) return 'bg-red-50 text-red-600'
  if (pct >= 0.7) return 'bg-amber-50 text-amber-600'
  return 'bg-green-50 text-green-600'
}

const TableList = ({
  tables,
  selectedTableId,
  currentTableId,
  isTableFull,
  onSelect,
}: CanvasProps) => {
  const assignable = tables.filter((t) => !t.type || t.type === 'table')

  const sorted = [...assignable].sort((a, b) =>
    (a.tableNumber || '').localeCompare(b.tableNumber || '', undefined, {
      numeric: true,
    }),
  )

  // Pin currently-assigned table to top
  const pinned = sorted.filter((t) => t.id === currentTableId)
  const rest = sorted.filter((t) => t.id !== currentTableId)
  const ordered = [...pinned, ...rest]

  return (
    <div className="h-full overflow-y-auto">
      {ordered.map((table) => {
        const isCurrent = table.id === currentTableId
        const isSelected = table.id === selectedTableId
        const full = isTableFull(table)
        const assigned = table.assignedCount ?? 0
        const capacity = table.capacity ?? 10

        return (
          <button
            key={table.id}
            onClick={() => onSelect(isSelected ? '' : table.id || '')}
            disabled={full}
            className={[
              'border-forest-light/10 flex w-full items-center justify-between border-b px-6 py-4 text-left transition-all',
              full
                ? 'cursor-not-allowed opacity-40'
                : isSelected
                  ? 'border-l-rose-dark bg-rose-dark/15 border-l-4'
                  : isCurrent
                    ? 'border-l-rose-dark/50 bg-rose-dark/8 border-l-4'
                    : 'hover:bg-forest-light/5 active:bg-forest-light/8',
            ].join(' ')}
          >
            <div>
              <p className="text-forest-light text-base font-bold">
                โต๊ะ {table.tableNumber}
              </p>
              <p className="text-forest-light/50 mt-0.5 text-xs font-semibold">
                {[table.notes, isCurrent ? 'กำลังนั่งอยู่' : null]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>

            <span
              className={`rounded-full px-3 py-1.5 text-xs font-bold ${occupancyColor(assigned, capacity)}`}
            >
              {assigned}/{capacity}
            </span>
          </button>
        )
      })}
    </div>
  )
}

export const KioskTablePicker = ({
  isOpen,
  onClose,
  guest,
  onSuccess,
}: KioskTablePickerProps) => {
  const params = useParams({ strict: false }) as { eventId?: string }
  const weddingId = params.eventId ?? ''
  const [mode, setMode] = useState<Mode>('map')
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null)

  const { data: tablesData, isLoading } = useQuery({
    ...getApiV1WeddingsByWeddingIdTablesMasterAllOptions({
      path: { weddingId },
    }),
    enabled: isOpen && !!weddingId,
  })

  // getApiV1WeddingsByWeddingIdTablesMasterAllOptions returns Array<TableMasterResponse> directly (no .data wrapper)
  const tables = (tablesData as any)?.data ?? tablesData ?? []
  const guestSeats = guest.followers ?? 1

  const isTableFull = (table: GinTableMasterResponse): boolean => {
    const isCurrentTable = table.id === guest.currentTableId
    const occupancyWithoutGuest = isCurrentTable
      ? (table.assignedCount ?? 0) - guestSeats
      : (table.assignedCount ?? 0)
    return occupancyWithoutGuest + guestSeats > (table.capacity ?? 10)
  }

  useEffect(() => {
    if (isOpen) {
      setSelectedTableId(null)
      setMode('map')
    }
  }, [isOpen])

  const selectedTable = tables.find((t: any) => t.id === selectedTableId)

  const queryClient = useQueryClient()

  const assignMutation = useMutation(
    postApiV1WeddingsByWeddingIdAdminTablesAssignMutation(),
  )
  const unassignMutation = useMutation(
    postApiV1WeddingsByWeddingIdAdminTablesUnassignMutation(),
  )
  const isPending = assignMutation.isPending || unassignMutation.isPending

  const handleConfirm = async () => {
    if (!selectedTableId || !selectedTable) return
    try {
      await assignMutation.mutateAsync({
        path: { weddingId },
        body: {
          targetId: guest.id,
          tableId: selectedTableId,
        },
      })
      toast.success('จัดโต๊ะเรียบร้อยแล้ว')
      queryClient.invalidateQueries({
        queryKey: getApiV1WeddingsByWeddingIdGuestsQueryKey({
          path: { weddingId },
        }),
      })
      queryClient.invalidateQueries({
        queryKey: getApiV1WeddingsByWeddingIdTablesMasterAllQueryKey({
          path: { weddingId },
        }),
      })
      onSuccess({
        tableId: selectedTableId,
        tableNumber: selectedTable.tableNumber,
      })
      onClose()
    } catch (err: unknown) {
      const apiErr = err as { body?: { error?: string } }
      toast.error(apiErr.body?.error ?? 'เกิดข้อผิดพลาดในการจัดโต๊ะ')
    }
  }

  const handleUnassign = async () => {
    if (!window.confirm('คุณต้องการลบรายชื่อแขกออกจากโต๊ะใช่หรือไม่?')) return
    try {
      await unassignMutation.mutateAsync({
        path: { weddingId },
        body: { targetId: guest.id },
      })
      toast.success('ยกเลิกการจัดโต๊ะเรียบร้อยแล้ว')
      queryClient.invalidateQueries({
        queryKey: getApiV1WeddingsByWeddingIdGuestsQueryKey({
          path: { weddingId },
        }),
      })
      queryClient.invalidateQueries({
        queryKey: getApiV1WeddingsByWeddingIdTablesMasterAllQueryKey({
          path: { weddingId },
        }),
      })
      onSuccess(null)
      onClose()
    } catch (err: unknown) {
      const apiErr = err as { body?: { error?: string } }
      toast.error(apiErr.body?.error ?? 'เกิดข้อผิดพลาดในการยกเลิกการจัดโต๊ะ')
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="flex h-[85vh] max-h-[85vh] w-full flex-col overflow-hidden bg-white p-0 font-sans sm:max-w-4xl"
      >
        {/* Accessibility labels (visually hidden) */}
        <DialogTitle className="sr-only">เลือกโต๊ะที่นั่ง</DialogTitle>
        <DialogDescription className="sr-only">
          เลือกโต๊ะที่นั่งสำหรับคุณ{guest.fullName}
        </DialogDescription>

        {/* ── Header ─────────────────────────────────────────── */}
        <div className="border-forest-light/10 flex shrink-0 items-center justify-between border-b px-6 py-4">
          <div>
            <p className="text-forest-light/60 text-[10px] font-bold tracking-widest uppercase">
              เลือกโต๊ะที่นั่ง
            </p>
            <p className="text-forest-light text-xl font-bold">
              คุณ{guest.fullName}
            </p>
            {guest.tableNumber && (
              <p className="text-forest-light/50 text-xs font-semibold">
                ปัจจุบัน: โต๊ะ {guest.tableNumber}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Mode toggle */}
            <div className="bg-forest-light/8 flex gap-1 rounded-full p-1">
              {(['map', 'list'] as Mode[]).map((modeKey) => (
                <button
                  key={modeKey}
                  onClick={() => setMode(modeKey)}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all ${
                    mode === modeKey
                      ? 'bg-rose-dark text-white shadow'
                      : 'text-forest-light/60 hover:text-forest-light'
                  }`}
                >
                  {modeKey === 'map' ? 'แผนที่' : 'รายการ'}
                </button>
              ))}
            </div>

            <button
              onClick={onClose}
              type="button"
              className="border-forest-light/15 bg-forest-light/5 text-forest-light/70 hover:bg-forest-light/10 flex size-9 items-center justify-center rounded-full border transition-all active:scale-95"
              aria-label="ปิด"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        {/* ── Content ─────────────────────────────────────────── */}
        <div className="min-h-0 flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <p className="text-forest-light/60 text-sm font-bold">
                กำลังโหลด...
              </p>
            </div>
          ) : mode === 'map' ? (
            <MapCanvas
              tables={tables}
              selectedTableId={selectedTableId}
              currentTableId={guest.currentTableId}
              isTableFull={isTableFull}
              onSelect={setSelectedTableId}
            />
          ) : (
            <TableList
              tables={tables}
              selectedTableId={selectedTableId}
              currentTableId={guest.currentTableId}
              isTableFull={isTableFull}
              onSelect={setSelectedTableId}
            />
          )}
        </div>

        {/* ── Bottom bar ─────────────────────────────────────── */}
        <div className="border-forest-light/10 flex shrink-0 gap-3 border-t bg-[#fdf4f5] px-6 py-4">
          {guest.currentTableId && (
            <button
              type="button"
              onClick={handleUnassign}
              disabled={isPending}
              className="rounded-2xl border-2 border-red-300 bg-white px-4 py-3 text-sm font-bold text-red-600 transition-all active:scale-95 disabled:opacity-50"
            >
              {unassignMutation.isPending ? 'กำลังยกเลิก...' : 'ถอดออกจากโต๊ะ'}
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedTableId || isPending}
            className="bg-rose-dark shadow-rose-dark/25 flex-1 rounded-2xl py-3 text-sm font-bold text-white shadow-lg transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {assignMutation.isPending
              ? 'กำลังจัดโต๊ะ...'
              : selectedTable
                ? `ยืนยัน โต๊ะ ${selectedTable.tableNumber}`
                : 'เลือกโต๊ะ'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
